import { createRouter, createWebHistory } from 'vue-router'
import { nextTick } from 'vue'
import { useAuthStore } from '@/stores/auth'
import { isAdminPortal, isClientPortal } from '@/utils/domain'
import {
  isChunkLoadError,
  noteNavigationSucceeded,
  reportChunkError,
} from '@/utils/chunkErrorHandling'
import { behandelaarRoutes } from './behandelaar-routes'
import { portalRoutes } from './portal-routes'
import { adminRoutes } from './admin-routes'
import { legacyRedirectPaths } from './legacy-redirects'
import { portalAuthGuard } from './portal-guard'
import { behandelaarAuthGuard } from './behandelaar-guard'
import { adminAuthGuard } from './admin-guard'
import { captureAgendaViewState } from './agenda-view-state'

// Select routes based on domain
function selectRoutes() {
  if (isAdminPortal) return adminRoutes
  if (isClientPortal) return portalRoutes
  return behandelaarRoutes
}
const routes = selectRoutes()

// Store scroll positions per route for back/forward navigation
const scrollPositions = new Map<string, number>()

// Track whether current navigation is from browser back/forward
let isPopStateNavigation = false
window.addEventListener('popstate', () => {
  isPopStateNavigation = true
})

const router = createRouter({
  history: createWebHistory(import.meta.env.BASE_URL),
  routes,
  // Note: scrollBehavior only works for window scroll. Since .app__main is
  // the actual scrollable container (window has overflow: hidden), we handle
  // scrolling manually in afterEach hook below.
  scrollBehavior() {
    return false
  }
})

// Save scroll position before leaving a route
router.beforeEach((to, from, next) => {
  // Save current scroll position for the route we're leaving
  if (from.fullPath) {
    const main = document.querySelector('.app__main')
    if (main) {
      scrollPositions.set(from.fullPath, main.scrollTop)
    }
  }
  next()
})

// Navigation guard to protect authenticated routes
router.beforeEach(async (to, from, next) => {
  const authStore = useAuthStore()

  // Wait for auth state to finish loading
  if (authStore.loading) {
    await new Promise<void>((resolve) => {
      const unwatch = authStore.$subscribe(() => {
        if (!authStore.loading) {
          unwatch()
          resolve()
        }
      })
      // Also check immediately in case it's already done
      if (!authStore.loading) {
        unwatch()
        resolve()
      }
    })
  }

  // Use admin-specific auth guard for admin portal
  if (isAdminPortal) {
    return adminAuthGuard(to, from, next)
  }

  // Use portal-specific auth guard for client portal
  if (isClientPortal) {
    return portalAuthGuard(to, from, next)
  }

  // Use behandelaar-specific auth guard for behandelaar app
  return behandelaarAuthGuard(to, from, next)
})

// Handle chunk loading errors after deployments (router-level fallback).
// Primary handlers are in utils/chunkErrorHandling.ts (vite:preloadError, unhandledrejection).
// This handler catches errors that reach Vue Router's error boundary.
router.onError((error, to) => {
  if (!isChunkLoadError(error)) return

  // Reload once, then tell the user — decided in one place for all three
  // handlers (#4181), and only once the document has proved it is staying
  // (#4409). `to.fullPath` is where the user was going, so the reload lands
  // there rather than on the route they were leaving.
  reportChunkError(to.fullPath)
})

// Handle scroll behavior for .app__main container and clear chunk error reload flag
router.afterEach((to, from) => {
  // Re-arm the chunk error handling, and drop a reaction that is still waiting:
  // the user is on the page they asked for
  noteNavigationSucceeded()

  // Remember the agenda's view-state so an in-app link back to it restores
  // where the user was instead of resetting to today (#3244)
  captureAgendaViewState(to, from)

  // Track legacy redirect usage for deprecation monitoring
  // See legacy-redirects.ts for deprecation timeline
  if (to.redirectedFrom) {
    const redirectedPath = to.redirectedFrom.path
    // Check if this was a redirect from a legacy route (matching path pattern)
    const isLegacyRedirect = Array.from(legacyRedirectPaths).some(pattern => {
      // Convert route pattern to regex (replace :param with [^/]+)
      const regexPattern = pattern.replace(/:[^/]+/g, '[^/]+')
      const regex = new RegExp(`^${regexPattern}$`)
      return regex.test(redirectedPath)
    })
    if (isLegacyRedirect) {
      console.info(`[Router] Legacy redirect: ${redirectedPath} → ${to.fullPath}`)
    }
  }

  nextTick(() => {
    const main = document.querySelector('.app__main')
    if (!main) return

    // Handle hash navigation (scroll to anchor with offset for sticky header)
    if (to.hash) {
      // An id lookup, not a selector: a hash carrying a parameter is not a valid selector.
      const targetElement = document.getElementById(decodeURIComponent(to.hash.slice(1)))
      if (targetElement) {
        const headerOffset = 80
        const elementPosition = targetElement.getBoundingClientRect().top
        const offsetPosition = main.scrollTop + elementPosition - headerOffset
        main.scrollTo({ top: offsetPosition, behavior: 'smooth' })
      }
      return
    }

    // Restore scroll position for back/forward navigation
    if (isPopStateNavigation) {
      const savedPosition = scrollPositions.get(to.fullPath)
      if (savedPosition !== undefined) {
        main.scrollTop = savedPosition
      } else {
        main.scrollTop = 0
      }
      isPopStateNavigation = false
      return
    }

    // Default: scroll to top for new navigation
    main.scrollTop = 0
  })
})

export default router
